import React, { useState } from "react";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import MoreHoriz from "@mui/icons-material/MoreHoriz";

/**
 * MemberMenu
 * 各メンバー行に表示される 3点メニューコンポーネント。
 * 「スキルシート」「編集」「削除」「登録メール送信」のアクションをまとめて提供する。
 */
export default function MemberMenu({
  user,
  currentCompanyUser,
  onDetail,
  onEdit,
  onDelete,
  onRestore,
  onSendRegisterMail,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  // Menu が開いているかどうか
  const open = Boolean(anchorEl);

  // 権限判定
  const isAdmin = currentCompanyUser?.role === "admin";
  const isGeneral = currentCompanyUser?.role === "general";
  const isManagerOfTargetUser = user.manager_id === currentCompanyUser?.id;

  const canEditUser = isAdmin || (isGeneral && isManagerOfTargetUser);
  const canDeleteUser = isAdmin;

  // 登録メール送信は admin / general のみ
  const canSendRegisterMail = isAdmin || isGeneral;

  // 管理者は全員閲覧可能
  // 担当者(general)は自分の担当のみスキルシート操作可能
  const canOpenSkillSheet = isAdmin || (isGeneral && isManagerOfTargetUser);

  // スキルシート作成済み判定
  const hasSkillSheet = Boolean(user.skill_sheet_flag);

  // メールアドレスが登録されているか
  const hasEmail = Boolean(user.email);

  // メンバーが削除されているか
  const isDeleted = Boolean(user.deleted_at);

  const skillSheetLabel = hasSkillSheet
    ? "スキルシートを見る"
    : "スキルシートを編集する";

  const handleClose = () => setAnchorEl(null);

  const registerMailTooltip = (() => {
    if (!canSendRegisterMail) return "管理者または担当者のみ送信できます";
    if (!hasEmail) return "メールアドレスが未設定です";
    return "";
  })();

  return (
    <>
      {/* 3点ボタン */}
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreHoriz fontSize="small" />
      </IconButton>

      {/* ドロップダウンメニュー */}
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {/* スキルシート詳細メニュー */}
        {hasSkillSheet ? (
          // 作成済み → 全員閲覧OK
          <MenuItem
            onClick={() => {
              onDetail(user);
              handleClose();
            }}
          >
            {skillSheetLabel}
          </MenuItem>
        ) : canOpenSkillSheet ? (
          // 未作成だけど担当者 → 編集OK
          <MenuItem
            onClick={() => {
              onDetail(user);
              handleClose();
            }}
          >
            {skillSheetLabel}
          </MenuItem>
        ) : (
          // 未作成 & 担当者じゃない → NG（閲覧も編集もできない）
          <Tooltip title="担当者しか編集できません" placement="top">
            <span>
              <MenuItem disabled>{skillSheetLabel}</MenuItem>
            </span>
          </Tooltip>
        )}

        {/* 編集：権限があるユーザーのみ */}
        {canEditUser && (
          <MenuItem
            onClick={() => {
              onEdit(user);
              handleClose();
            }}
          >
            メンバー情報を編集する
          </MenuItem>
        )}

        {/* 削除済みメンバーは復元を表示 */}
        {isDeleted ? (
          <MenuItem
            onClick={() => {
              onRestore(user);
              handleClose();
            }}
          >
            メンバーを復元する
          </MenuItem>
        ) : (
          /* 未削除メンバーは登録メール送信を表示 */
          <Tooltip title={registerMailTooltip} placement="top">
            <span>
              <MenuItem
                disabled={!canSendRegisterMail || !hasEmail}
                onClick={() => {
                  if (!canSendRegisterMail || !hasEmail) return;
                  onSendRegisterMail(user);
                  handleClose();
                }}
              >
                登録メールを送る
              </MenuItem>
            </span>
          </Tooltip>
        )}

        {/* 削除：管理者のみ */}
        {canDeleteUser && !isDeleted && (
          <MenuItem
            onClick={() => {
              onDelete(user);
              handleClose();
            }}
            sx={{ color: "error.main" }}
          >
            メンバーを削除する
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
